<template>
  <div class="app-container custom-cursor">
    <div class="cursor" ref="cursor"></div>
    <Navbar />
    <main class="content">
      <router-view v-slot="{ Component }">
        <transition
          name="page"
          mode="out-in"
          @before-enter="beforeEnter"
          @after-enter="afterEnter"
        >
          <component :is="Component" class="page-content" />
        </transition>
      </router-view>
    </main>
    <Footer />
    
    <!-- Scroll to top button -->
    <button 
      class="scroll-to-top" 
      @click="scrollToTop" 
      :class="{ 'visible': showScrollButton }"
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
        <path fill="white" d="M12 4l-8 8h5v8h6v-8h5z" />
      </svg>
    </button>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, onUnmounted } from 'vue';
import Navbar from './components/Navbar.vue';
import Footer from './components/Footer.vue';
// import LanguageTest from './components/LanguageTest.vue';

export default defineComponent({
  components: {
    Navbar,
    Footer,
    // LanguageTest
  },
  setup() {
    const cursor = ref<HTMLElement | null>(null);
    const showScrollButton = ref(false);

    const moveCursor = (e: MouseEvent): void => {
      const { clientX, clientY } = e;
      if (cursor.value) {
        cursor.value.style.left = `${clientX}px`;
        cursor.value.style.top = `${clientY}px`;
      }
    };

    const createClickAnimation = (e: MouseEvent): void => {
      const { clientX, clientY } = e;
      
      // Create multiple cloud elements for the animation
      for (let i = 0; i < 5; i++) {
        const cloud = document.createElement('div');
        cloud.className = 'click-cloud';
        
        // Randomize the cloud appearance and animation
        const size = Math.random() * 20 + 10;
        const direction = Math.random() > 0.5 ? 'up' : 'down';
        const xOffset = (Math.random() - 0.5) * 50;
        
        cloud.style.width = `${size}px`;
        cloud.style.height = `${size}px`;
        cloud.style.left = `${clientX + xOffset}px`;
        cloud.style.top = `${clientY}px`;
        cloud.style.animationName = `float-${direction}`;
        cloud.style.animationDuration = `${Math.random() * 2 + 1}s`;
        
        document.body.appendChild(cloud);
        
        // Remove the cloud element after animation completes
        setTimeout(() => {
          document.body.removeChild(cloud);
        }, 3000);
      }
    };

    const scrollToTop = (): void => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    };

    const handleScroll = (): void => {
      showScrollButton.value = window.scrollY > 300;
    };

    const beforeEnter = (): void => {
      // Reset scroll position before entering new page
      window.scrollTo(0, 0);
    };

    const afterEnter = (): void => {
      // Re-enable scroll after transition
      document.body.style.overflow = '';
    };

    onMounted(() => {
      if (window.matchMedia("(pointer: fine)").matches) {
        document.addEventListener('mousemove', moveCursor);
        document.addEventListener('click', createClickAnimation);
      }
      
      // Add scroll event listener
      window.addEventListener('scroll', handleScroll);
    });

    onUnmounted(() => {
      if (window.matchMedia("(pointer: fine)").matches) {
        document.removeEventListener('mousemove', moveCursor);
        document.removeEventListener('click', createClickAnimation);
      }
      
      // Remove scroll event listener
      window.removeEventListener('scroll', handleScroll);
    });

    return {
      cursor,
      showScrollButton,
      scrollToTop,
      beforeEnter,
      afterEnter
    };
  }
});
</script>

<style>
/* Custom scrollbar styles */
::-webkit-scrollbar {
  width: 12px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: #009FE3 !important;
  border-radius: 6px;
}

::-webkit-scrollbar-thumb:hover {
  background: #007bb3 !important;
}

/* Custom cursor styles */
.custom-cursor {
  cursor: none;
}

.cursor {
  width: 50px;
  height: 50px;
  border-radius: 0;
  position: fixed;
  pointer-events: none;
  z-index: 9999;
  mix-blend-mode: normal;
  padding: 0;
  display: none;
  background: url('../public/logo.png') no-repeat center center;
  background-size: contain;
}

/* Click animation cloud elements */
.click-cloud {
  position: fixed;
  pointer-events: none;
  z-index: 9998;
  background: url('../public/cloud.png') no-repeat center center;
  background-size: contain;
  opacity: 0.8;
  animation-timing-function: ease-out;
  animation-fill-mode: forwards;
}

@keyframes float-up {
  0% {
    transform: translateY(0) scale(0.5);
    opacity: 0.8;
  }
  100% {
    transform: translateY(-100px) scale(1.5);
    opacity: 0;
  }
}

@keyframes float-down {
  0% {
    transform: translateY(0) scale(0.5);
    opacity: 0.8;
  }
  100% {
    transform: translateY(100px) scale(1.5);
    opacity: 0;
  }
}

@media (pointer: fine) {
  .cursor {
    display: block;
  }
}

/* Ensure the custom cursor doesn't interfere with text selection */
* {
  cursor: inherit;
}

/* Override cursor for clickable elements */
a, button, input[type="submit"], input[type="button"], .clickable {
  cursor: pointer;
}

/* Page transition styles */
.page-content {
  position: relative;
  min-height: 100vh;
}

.page-enter-active,
.page-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: absolute;
  width: 100%;
}

.page-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.page-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

/* Scroll to top button styles */
.scroll-to-top {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 3rem;
  height: 3rem;
  background-color: #009FE3 !important;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease, visibility 0.3s ease, transform 0.3s ease;
  z-index: 1000;
}

.scroll-to-top.visible {
  opacity: 1;
  visibility: visible;
}

.scroll-to-top:hover {
  transform: translateY(-5px);
}
</style>